import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, trackClick, useAnalytics } from '@bonusly/analytics';
import { AnnouncementActionEvent } from '@bonusly/common-announcements';
import {
	AwardRibbonOutline,
	BookmarkOutline,
	CogOutline,
	HomeOutline,
	MegaphoneOutline,
	PlantOutline,
	ShoppingCartOutline,
	Text,
	UserGroupOutline,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useBuildMonarchURL, useBuildRecognizeURL } from '@bonusly/url';
import {
	enabled,
	Permission,
	ProtectedContent,
	ProtectedContentWithPermissionOrFeatureFlag,
	useCurrentCompany,
	useCurrentUser,
	usePermission,
} from '@bonusly/who-am-i';

import { Reveal } from '../../../../components/display/reveal';
import { useIsRecognize } from '../../../../hooks/use-is-recognize';
import { AppPath, Route } from '../../config/router';
import { Variant } from '../FeatureFlag';
import { MenuItemInformation } from '../Menu/MenuItemInformation';
import { NavDivider } from './NavDivider';
import { NavGroup } from './NavGroup';
import { NavMenuItem } from './NavMenuItem';
import { NavMenuLink } from './NavMenuLink';

export const NavMenuOptionsMobile = ({ onClose }: { onClose: VoidFunction }) => {
	const recognizeURL = useBuildRecognizeURL();
	const monarchURL = useBuildMonarchURL();
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const { t } = useTranslation('navigation');
	const isRecognize = useIsRecognize();
	const dispatch = useDispatch();
	const { trackEvent } = useAnalytics();
	const canAccessTeamHub = usePermission(Permission.CanAccessTeamHub);

	const isLoaded = !!user && !!company;

	if (!isLoaded) return null;

	const handleAnnouncementsClick = () => {
		dispatch(AnnouncementActionEvent.OpenAnnouncement);
		onClose();
	};

	const settingsUrl = enabled('user-settings-redesign') ? '/user-settings' : recognizeURL(Route.Settings);

	return (
		<>
			<NavGroup data-testid="nav-menu-options-mobile">
				<NavMenuItem colorScheme="blue" icon={<HomeOutline />} to={AppPath.Home}>
					{t('user_menu.home')}
				</NavMenuItem>

				<ProtectedContentWithPermissionOrFeatureFlag
					flag={[Variant.OneOnOnesEarlyAccess, Variant.OneOnOnesLimitedRelease]}
					permissions={Permission.CanAccessOneOnOnes}
				>
					<NavMenuLink colorScheme="blue" icon={<PlantOutline />} onClick={onClose} to={isRecognize ? monarchURL('/growth') : '/growth'}>
						{t('links.growth')}
					</NavMenuLink>
				</ProtectedContentWithPermissionOrFeatureFlag>

				<Reveal if={canAccessTeamHub}>
					<NavMenuLink
						colorScheme="blue"
						icon={<UserGroupOutline />}
						onClick={() => {
							trackEvent(AnalyticsEvent.TopNavbar.TeamHubNavigation, { pathway: 'menu' });
							onClose();
						}}
						to={isRecognize ? monarchURL('/team/your-team') : '/team/your-team'}
					>
						{t('links.team')}
					</NavMenuLink>
				</Reveal>

				<NavMenuLink
					colorScheme="blue"
					icon={<ShoppingCartOutline />}
					onClick={onClose}
					to={isRecognize ? monarchURL(AppPath.Rewards) : AppPath.Rewards}
					{...trackClick(AnalyticsEvent.Rewards.ClickRewardsLink, {
						source: 'nav',
						userBalance: user.earning_balance,
					})}
				>
					<MenuItemInformation
						label={
							<Text as="span" color="gray.700" fontSize="md">
								{t('user_menu.rewards')}
							</Text>
						}
						subLabel={`${user.earning_balance_with_currency} available`}
					/>
				</NavMenuLink>

				<ProtectedContent permissions={Permission.CanAccessIncentives}>
					<NavMenuItem colorScheme="blue" icon={<AwardRibbonOutline />} onClick={onClose} to="/incentives">
						{t('user_menu.incentives')}
					</NavMenuItem>
				</ProtectedContent>
			</NavGroup>

			<NavDivider />

			<NavGroup>
				<NavMenuItem icon={<MegaphoneOutline />} onClick={handleAnnouncementsClick}>
					{t('links.announcements')}
				</NavMenuItem>

				<NavMenuItem icon={<BookmarkOutline />} to={AppPath.Bookmark} onClick={onClose}>
					{t('user_menu.bookmarks')}
				</NavMenuItem>

				<NavMenuItem icon={<CogOutline />} onClick={onClose} to={settingsUrl}>
					{t('user_menu.profile_settings')}
				</NavMenuItem>
			</NavGroup>
		</>
	);
};
